import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGuaranteesPdfPreview } from '~/modules/guarantees/form/application/shared/hooks/useGuaranteesPdfPreview.ts';
import { downloadGuaranteesClientDocument } from '~/store/guarantees/thunks/applicationThunks.ts';
import type { TGuaranteesApplicationFile } from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import { UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT } from './constants.ts';
import { getApprovalLetterPreviewFileId } from './utils.ts';

type TPreviewDocument = {
  data: Blob;
  contentType?: string;
  fileId: string;
};

type TSetModalAlert = (value: { text: string }) => void;

export const shouldLoadInternalApprovalPdfPreview = ({
  currentPreviewFileId,
  documentFileId,
  hasApplicationFilesLoaded,
}: {
  currentPreviewFileId?: string;
  documentFileId: string;
  hasApplicationFilesLoaded: boolean;
}): boolean =>
  hasApplicationFilesLoaded && !!documentFileId && currentPreviewFileId !== documentFileId;

export const isInternalApprovalPdfPreviewLoading = ({
  hasApplicationFilesLoaded,
  hasPreviewDocument,
  isPdfPreviewLoading,
  isPreviewLoading,
}: {
  hasApplicationFilesLoaded: boolean;
  hasPreviewDocument: boolean;
  isPdfPreviewLoading: boolean;
  isPreviewLoading: boolean;
}): boolean =>
  (!hasApplicationFilesLoaded && !hasPreviewDocument) ||
  isPreviewLoading ||
  isPdfPreviewLoading;

export const useInternalApprovalPdfPreview = ({
  approvalLetterFile,
  hasApplicationFilesLoaded,
  setModalAlert,
  showGuaranteesRequestError,
}: {
  approvalLetterFile?: TGuaranteesApplicationFile | null;
  hasApplicationFilesLoaded: boolean;
  setModalAlert: TSetModalAlert;
  showGuaranteesRequestError: (error: unknown) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [previewDocument, setPreviewDocument] = useState<TPreviewDocument | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const documentFileId = getApprovalLetterPreviewFileId(approvalLetterFile);
  const pdfPreview = useGuaranteesPdfPreview({
    contentType: previewDocument?.contentType,
    documentBlob: previewDocument?.data,
    missingPdfText: UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT,
    setModalAlert,
  });
  const isDocumentLoading = isInternalApprovalPdfPreviewLoading({
    hasApplicationFilesLoaded,
    hasPreviewDocument: !!previewDocument,
    isPdfPreviewLoading: pdfPreview.isDocumentLoading,
    isPreviewLoading,
  });

  useEffect(() => {
    if (!hasApplicationFilesLoaded) {
      return;
    }

    if (!documentFileId) {
      setPreviewDocument(null);
      setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT });

      return;
    }

    if (
      !shouldLoadInternalApprovalPdfPreview({
        currentPreviewFileId: previewDocument?.fileId,
        documentFileId,
        hasApplicationFilesLoaded,
      })
    ) {
      return;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      setPreviewDocument(null);
      setIsPreviewLoading(true);

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: documentFileId,
            isAttached: false,
          })
        ).unwrap();

        if (isCancelled) {
          return;
        }

        setPreviewDocument({
          data: downloadedDocument.data,
          contentType: downloadedDocument.contentType,
          fileId: documentFileId,
        });
      } catch (error) {
        if (!isCancelled) {
          setPreviewDocument(null);
          showGuaranteesRequestError(error);
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      isCancelled = true;
    };
  }, [
    documentFileId,
    dispatch,
    hasApplicationFilesLoaded,
    previewDocument?.fileId,
    setModalAlert,
    showGuaranteesRequestError,
  ]);

  return {
    handleOpenPdf: pdfPreview.handleOpenPdf,
    isDocumentLoading,
    pdfUrl: pdfPreview.pdfUrl,
  };
};
